<template>
  <section
    v-if="showSection"
    id="interview"
    class="lpu-interview d-flex flex-column"
    :class="isMobile ? 'lpu-interview--mobile ga-4' : 'ga-8'"
    data-qa="lpu_interview"
  >
    <h2
      class="text--text ma-0"
      :class="isMobile ? 'text-h6' : 'text-h5'"
      data-qa="lpu_interview_title"
    >
      {{ tStatic('Интервью с руководителем') }}
      <a
        v-if="adminHref"
        :href="adminHref"
        class="adminka ml-2"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ tStatic('Править интервью') }}
      </a>
    </h2>

    <div
      class="lpu-interview__body"
      :class="isMobile ? 'd-flex flex-column ga-4' : 'd-flex align-start ga-6'"
    >
      <v-img
        v-if="photoSrc"
        contain
        :width="isMobile ? '100%' : 300"
        max-width="100%"
        :aspect-ratio="isMobile ? 4 / 3 : undefined"
        :src="photoSrc"
        :alt="photoAlt"
        class="rounded-30 bg-lpu-branding-color-background flex-grow-0 flex-shrink-0"
        data-qa="lpu_interview_photo"
      />

      <div class="lpu-interview__main d-flex flex-column min-w-0 ga-6">
        <div
          class="lpu-interview__text text-body-1 text--text text-break"
          :class="{ 'lpu-interview__text--expanded': isExpanded }"
          data-qa="lpu_interview_text"
          v-html="textHtml/* eslint-disable-line vue/no-v-html */"
        />

        <button
          v-if="!isMobile"
          type="button"
          class="lpu-interview__more align-self-start text-link-1 text-decoration-underline primary--text"
          data-qa="lpu_interview_toggle"
          @click="toggleExpanded"
        >
          {{ desktopToggleLabel }}
        </button>

        <v-btn
          v-else
          depressed
          height="52"
          :block="true"
          class="lpu-interview__toggle branding-btn branding-btn--primary rounded-20 text-subtitle-1 text-none w-100"
          data-qa="lpu_interview_toggle"
          @click="toggleExpanded"
        >
          {{ mobileToggleLabel }}
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import getInterviewPhotoUrl from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInterview/functions/getInterviewPhotoUrl'

const isMobile = useIsMobile()
const { context }: ContextStore = useContextStore()
const isExpanded = ref(false)

const interview = computed(() => context.lpu.lpuinterview)
const textHtml = computed(() => {
  if (!interview.value) {
    return ''
  }

  return tDynamic(interview.value, 'text') || interview.value.text || ''
})
const showSection = computed(() => Boolean(textHtml.value))
const photoSrc = computed(() => getInterviewPhotoUrl(interview.value?.id))
const photoAlt = computed(() => tStatic('Фотография с изображением руководителя'))
const adminHref = computed(() => {
  const isSuperuser = Boolean(context.request.user.is_superuser)

  if (isMobile.value || !isSuperuser || !interview.value?.id) {
    return ''
  }

  return `/admin/doctors/lpuinterview/${interview.value.id}/`
})
const desktopToggleLabel = computed(() => (
  isExpanded.value
    ? tStatic('Скрыть')
    : tStatic('Показать еще')
))
const mobileToggleLabel = computed(() => (
  isExpanded.value
    ? tStatic('Свернуть')
    : tStatic('Развернуть')
))

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}
</script>

<style scoped lang="scss">
@import 'src/styles/variables';
@import 'src/styles/mixins';

.lpu-interview {
  &__main {
    flex: 1 1 auto;
  }

  &--mobile &__main {
    width: 100%;
  }

  &__text {
    position: relative;
    max-height: 400px;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 84px;
      pointer-events: none;

      @include hideGradientColor(255, 255, 255);
    }

    &--expanded {
      max-height: none;
      overflow: visible;

      &::after {
        display: none;
      }
    }

    :deep(.ask) {
      margin: 24px 0 0;
      font-size: 16px;
      font-weight: $weight-medium;
      line-height: 1.35;
    }

    :deep(.ask:first-child) {
      margin-top: 0;
    }

    :deep(.interview),
    :deep(p) {
      margin: 12px 0 0;
      font-size: 16px;
      font-weight: $weight-regular;
      line-height: 1.25;
    }

    :deep(p:first-child) {
      margin-top: 0;
    }

    :deep(.cit) {
      display: inline;
    }

    :deep(strong) {
      font-weight: $weight-medium;
    }
  }

  &--mobile &__text:not(&__text--expanded) {
    max-height: 240px;
  }

  &__more {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
